import { useRouter } from "@tanstack/react-router"
import { createColumnHelper, flexRender, useTable } from "@tanstack/react-table"
import { Badge } from "design-system/atoms/Badge"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import DateDisplay from "design-system/molecules/DateDisplay"
import {
  Table,
  TableBody,
  TableCell,
  TableEmptySlate,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from "design-system/molecules/Table"
import { features, type Features } from "lib/table"
import { Sparkles, Ticket } from "lucide-react"
import { useOpenBackofficeConversation } from "modules/backoffice/lib/useOpenBackofficeConversation"
import { AmphoraIcon } from "modules/shared/icons/amphora"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { BackofficeSupportQuestionsResponseDataSchema } from "schema"
import { z } from "zod"

type Question = z.infer<typeof BackofficeSupportQuestionsResponseDataSchema>[number]

const columnHelper = createColumnHelper<Features, Question>()

const SUMMARY_MAX_LENGTH = 50

type Props = {
  data: z.infer<typeof BackofficeSupportQuestionsResponseDataSchema> | undefined
  isPending: boolean
}

export function BackofficeSupportQuestionsTable({ data, isPending }: Props) {
  const { t } = useTranslation()
  const { buildLocation } = useRouter()
  const openConversation = useOpenBackofficeConversation()

  const columns = useMemo(
    () =>
      columnHelper.columns([
        columnHelper.accessor("createdAt", {
          header: t("backoffice.support.table.createdAt"),
          cell: ({ getValue }) => <DateDisplay date={getValue()} />,
        }),
        columnHelper.display({
          id: "company",
          header: t("backoffice.support.table.company"),
          cell: ({ row }) => {
            const { name, logo } = row.original.company
            return (
              <div className="flex items-center gap-2">
                <img src={logo ?? ""} style={{ height: 30 }} />
                <span>{name}</span>
              </div>
            )
          },
        }),
        columnHelper.accessor("subject", {
          header: t("backoffice.support.table.subject"),
          cell: ({ getValue }) => {
            const subject = getValue()
            if (!subject) return <span className="text-muted-foreground">{"—"}</span>

            const isTruncated = subject.length > SUMMARY_MAX_LENGTH
            const truncated = isTruncated ? `${subject.slice(0, SUMMARY_MAX_LENGTH)}…` : subject

            if (!isTruncated) return <span>{subject}</span>

            return (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="cursor-default">{truncated}</span>
                </TooltipTrigger>
                <TooltipContent className="max-w-sm">{subject}</TooltipContent>
              </Tooltip>
            )
          },
        }),
        columnHelper.accessor("summary", {
          header: t("backoffice.support.table.summary"),
          cell: ({ getValue }) => {
            const summary = getValue()
            const isTruncated = summary.length > SUMMARY_MAX_LENGTH
            const truncated = isTruncated ? `${summary.slice(0, SUMMARY_MAX_LENGTH)}…` : summary

            if (!isTruncated) return <span>{summary}</span>

            return (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="cursor-default">{truncated}</span>
                </TooltipTrigger>
                <TooltipContent className="max-w-sm">{summary}</TooltipContent>
              </Tooltip>
            )
          },
          meta: { colSpan: 2 },
        }),
        columnHelper.accessor("category", {
          header: t("backoffice.support.table.category"),
          cell: ({ getValue }) => {
            const category = getValue()
            if (!category) return <span className="text-muted-foreground">{"—"}</span>
            return <Badge variant="secondary">{category}</Badge>
          },
          meta: { align: "center" },
        }),
        columnHelper.accessor("automaticallySolved", {
          header: () => null,
          cell: ({ getValue, row }) => {
            const hasAutoSolved = getValue()
            const hasTicket = row.original.hasTicket
            const hasAmphoraTicketIntegration = row.original.hasAmphoraTicketIntegration

            if (!hasAutoSolved && !hasTicket && !hasAmphoraTicketIntegration) return null

            return (
              <div className="flex items-center justify-center gap-2">
                {hasAutoSolved && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Sparkles size={16} />
                    </TooltipTrigger>
                    <TooltipContent>{t("backoffice.support.table.autoSolved")}</TooltipContent>
                  </Tooltip>
                )}
                {hasTicket && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Ticket size={16} />
                    </TooltipTrigger>
                    <TooltipContent>{t("backoffice.support.table.hasTicket")}</TooltipContent>
                  </Tooltip>
                )}
                {hasAmphoraTicketIntegration && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex">
                        <AmphoraIcon className="size-4" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>{"Amphora"}</TooltipContent>
                  </Tooltip>
                )}
              </div>
            )
          },
          meta: { align: "center" as const },
        }),
      ]),
    [t],
  )

  const table = useTable({
    data: data ?? [],
    columns,
    features,
  })

  async function handleRowClick(row: Question) {
    const link = buildLocation({
      to: "/conversations/list/$customerId",
      params: { customerId: row.customerId },
      search: { tab: "support", conversationId: Number(row.conversationId), questionId: row.id },
    }).href

    try {
      await navigator.clipboard.writeText([window.location.origin, link].join(""))
    } catch (_error) {
      // Ignore clipboard errors, still attempt impersonation and navigation
    }

    openConversation.mutate({
      organizationId: row.organizationId,
      customerId: row.customerId,
      conversationId: row.conversationId,
      questionId: row.id,
    })
  }

  function renderContent() {
    if (isPending || data === undefined) {
      return <TableSkeleton colSpans={columns.map((c) => c.meta?.colSpan ?? 1)} />
    }

    if (data.length === 0) {
      return (
        <TableEmptySlate
          colSpan={columns.reduce((acc, c) => acc + (c.meta?.colSpan ?? 1), 0)}
          message={t("backoffice.support.table.empty")}
        />
      )
    }

    return table.getRowModel().rows.map((row) => (
      <TableRow
        key={row.id}
        onClick={() => handleRowClick(row.original)}
        className="cursor-pointer"
      >
        {row.getVisibleCells().map((cell) => (
          <TableCell key={cell.id} {...cell.column.columnDef.meta}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
    ))
  }

  return (
    <Table>
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <TableHead key={header.id} {...header.column.columnDef.meta}>
                {flexRender(header.column.columnDef.header, header.getContext())}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>{renderContent()}</TableBody>
    </Table>
  )
}
